import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getApiUrl } from '../../api'

interface ReservationItem {
  reservation_id: number
  space_id: number
  space_name: string
  reserve_date: string
  time_slot_id: number
  start_time: string | null
  end_time: string | null
  label: string | null
  headcount: number
  status: number
  created_at: string
}

const STATUS_INFO: Record<number, { label: string; color: string; bg: string; border: string }> = {
  0: { label: '예약 대기', color: '#b45309', bg: '#fef3c7', border: '#fde68a' },
  1: { label: '예약 완료', color: '#166534', bg: '#dcfce7', border: '#86efac' },
  2: { label: '취소됨',    color: '#6b7280', bg: '#f3f4f6', border: '#e5e7eb' },
}

function formatDate(d: string) {
  const date = new Date(d)
  const DAYS = ['일', '월', '화', '수', '목', '금', '토']
  return `${date.getFullYear()}. ${date.getMonth() + 1}. ${date.getDate()}. (${DAYS[date.getDay()]})`
}

function formatTime(t: string | null) {
  if (!t) return '-'
  return t.slice(0, 5)
}

function formatCreatedAt(d: string) {
  const date = new Date(d)
  return `${date.getFullYear()}. ${date.getMonth() + 1}. ${date.getDate()}. ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')} 신청`
}

function isWithin1Hour(reserveDate: string, endTime: string | null) {
  if (!endTime) return false
  const [h, m] = endTime.split(':').map(Number)
  const reserveDateTime = new Date(reserveDate)
  reserveDateTime.setHours(h, m, 0, 0)
  const now = new Date()
  const diffMs = reserveDateTime.getTime() - now.getTime()
  return diffMs > 0 && diffMs <= 60 * 60 * 1000
}

function isPast(reserveDate: string, endTime: string | null) {
  const [h, m] = endTime ? endTime.split(':').map(Number) : [23, 59]
  const reserveDateTime = new Date(reserveDate)
  reserveDateTime.setHours(h, m, 0, 0)
  return new Date() > reserveDateTime
}

export default function SpaceReservationCheck() {
  const navigate = useNavigate()
  const token = localStorage.getItem('access_token')
  const isLoggedIn = !!token

  // 회원
  const [memberReservations, setMemberReservations] = useState<ReservationItem[]>([])
  const [memberLoading, setMemberLoading] = useState(false)

  // 비회원
  const [guestName, setGuestName] = useState('')
  const [guestPhone, setGuestPhone] = useState('')
  const [guestReservations, setGuestReservations] = useState<ReservationItem[] | null>(null)
  const [guestSearched, setGuestSearched] = useState(false)
  const [guestLoading, setGuestLoading] = useState(false)
  const [guestError, setGuestError] = useState('')

  const [cancelModalId, setCancelModalId] = useState<number | null>(null)
  const [cancelling, setCancelling] = useState(false)

  useEffect(() => {
    if (isLoggedIn) fetchMemberReservations()
  }, [])

  const fetchMemberReservations = async () => {
    setMemberLoading(true)
    try {
      const res = await fetch(getApiUrl('/marubit/api/space/reservations/me'), {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.ok) {
        const data = await res.json()
        setMemberReservations(data.reservations ?? [])
      }
    } catch { /* ignore */ }
    setMemberLoading(false)
  }

  const handleGuestSearch = async () => {
    if (!guestName.trim()) { setGuestError('이름을 입력해주세요.'); return }
    if (!guestPhone.trim()) { setGuestError('전화번호를 입력해주세요.'); return }
    setGuestError('')
    setGuestLoading(true)
    try {
      const params = new URLSearchParams({ guest_name: guestName.trim(), guest_phone: guestPhone.trim() })
      // 비회원 조회는 키오스크 전용이라 회원 없이 조회 가능한 엔드포인트 필요
      // 현재는 kiosk 엔드포인트 사용 (토큰 없이 접근 가능한 엔드포인트로 교체 필요)
      const res = await fetch(getApiUrl(`/marubit/api/space/guest/check?${params.toString()}`))
      if (res.ok) {
        const data = await res.json()
        setGuestReservations(data.reservations ?? [])
      } else {
        setGuestReservations([])
      }
    } catch {
      setGuestReservations([])
    }
    setGuestSearched(true)
    setGuestLoading(false)
  }

  const handleCancel = async () => {
    if (!cancelModalId) return
    setCancelling(true)
    try {
      const res = await fetch(getApiUrl(`/marubit/api/space/reservations/${cancelModalId}/cancel`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.ok) {
        setMemberReservations(prev =>
          prev.map(r => r.reservation_id === cancelModalId ? { ...r, status: 2 } : r)
        )
        setCancelModalId(null)
      } else {
        const data = await res.json()
        alert(data.detail || '취소 중 오류가 발생했습니다.')
      }
    } catch {
      alert('서버 연결 오류가 발생했습니다.')
    }
    setCancelling(false)
  }

  const reservations = isLoggedIn ? memberReservations : (guestReservations ?? [])

  return (
    <>
    <div className="bg-[#fafafa] min-h-screen px-6 py-12">
      <div className="max-w-[1200px] mx-auto">
        <div className="mb-8">
          <h1 className="text-[26px] font-extrabold text-[#5a4035] mb-2">공간 예약 확인</h1>
          <p className="text-[14px] text-gray-400">신청한 공간 예약 내역을 확인할 수 있습니다.</p>
        </div>

        {/* 안내 배너 */}
        <div className="bg-[#fef9f6] border border-[#e8d5c8] rounded-2xl px-5 py-4 mb-6 text-[13px] text-[#7a5c4e] leading-relaxed space-y-1">
          <p>📋 <span className="font-semibold">예약 완료</span> 상태여야 공간을 이용할 수 있습니다.</p>
          <p>📞 사용 1시간 전까지 <span className="font-semibold">예약 대기</span> 상태라면 화면하단 <span className="font-semibold text-[#887267]">대표번호</span>로 문의해주세요.</p>
          {!isLoggedIn && <p>❌ 예약 취소는 대표번호로 문의해주세요.</p>}
        </div>

        {/* 비회원 검색 */}
        {!isLoggedIn && (
          <div className="bg-white rounded-2xl p-6 shadow-[0_2px_12px_rgba(136,114,103,0.08)] mb-6">
            <h2 className="text-[15px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-1">신청 내역 조회</h2>
            <p className="text-[13px] text-gray-400 mb-4 ml-[13px]">신청 시 입력한 이름과 전화번호를 입력해주세요.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={guestName}
                onChange={e => { setGuestName(e.target.value); setGuestError('') }}
                placeholder="이름"
                className="flex-1 h-11 px-4 border-[1.5px] border-[#e5e7eb] rounded-lg text-[14px] outline-none transition-colors"
                onFocus={e => (e.currentTarget.style.borderColor = '#887267')}
                onBlur={e => (e.currentTarget.style.borderColor = '#e5e7eb')}
                onKeyDown={e => e.key === 'Enter' && handleGuestSearch()}
              />
              <input
                type="tel"
                value={guestPhone}
                onChange={e => { setGuestPhone(e.target.value); setGuestError('') }}
                placeholder="전화번호"
                className="flex-1 h-11 px-4 border-[1.5px] border-[#e5e7eb] rounded-lg text-[14px] outline-none transition-colors"
                onFocus={e => (e.currentTarget.style.borderColor = '#887267')}
                onBlur={e => (e.currentTarget.style.borderColor = '#e5e7eb')}
                onKeyDown={e => e.key === 'Enter' && handleGuestSearch()}
              />
              <button
                onClick={handleGuestSearch}
                disabled={guestLoading}
                className="h-11 px-6 bg-[#887267] text-white rounded-lg text-[14px] font-bold hover:bg-[#6b5248] transition-colors whitespace-nowrap disabled:bg-[#b8a39b]"
              >
                {guestLoading ? '조회 중...' : '조회하기'}
              </button>
            </div>
            {guestError && <p className="text-[12px] text-red-500 mt-2">{guestError}</p>}
            <p className="text-center text-[13px] text-gray-400 mt-3">
              회원이신가요?{' '}
              <button onClick={() => navigate('/auth/login')} className="text-[#887267] font-semibold underline">로그인하기</button>
            </p>
          </div>
        )}

        {/* 로딩 */}
        {(memberLoading || guestLoading) && (
          <div className="text-center text-gray-300 py-12">불러오는 중...</div>
        )}

        {/* 결과 없음 */}
        {!memberLoading && !guestLoading && (isLoggedIn || guestSearched) && reservations.length === 0 && (
          <div className="bg-white rounded-2xl p-12 text-center text-gray-300 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
            예약 내역이 없습니다.
          </div>
        )}

        {/* 예약 목록 */}
        {!memberLoading && reservations.length > 0 && (
          <div className="space-y-4">
            <p className="text-[13px] text-gray-400">총 {reservations.length}건</p>
            {reservations.map(r => {
              const statusInfo = STATUS_INFO[r.status] ?? STATUS_INFO[2]
              const past = r.status !== 2 && isPast(r.reserve_date, r.end_time)
              const warn = r.status === 0 && isWithin1Hour(r.reserve_date, r.end_time)
              const displayStatus = past
                ? { label: '사용 완료', color: '#6b7280', bg: '#f3f4f6', border: '#e5e7eb' }
                : statusInfo

              return (
                <div key={r.reservation_id} className="bg-white rounded-2xl shadow-[0_2px_12px_rgba(136,114,103,0.08)] overflow-hidden">
                  {/* 상단 바 */}
                  <div className="flex items-center justify-between px-6 py-4 border-b border-[#f5f2f0]">
                    <div className="flex items-center gap-3">
                      <span
                        className="px-3 py-1 rounded-full text-[12px] font-bold border"
                        style={{ color: displayStatus.color, background: displayStatus.bg, borderColor: displayStatus.border }}
                      >
                        {displayStatus.label}
                      </span>
                      <span className="text-[15px] font-extrabold text-[#3d2f2a]">{r.space_name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[12px] text-gray-400">{formatCreatedAt(r.created_at)}</span>
                      {isLoggedIn && (r.status === 0 || r.status === 1) && !past && (
                        <button
                          onClick={() => setCancelModalId(r.reservation_id)}
                          className="text-[12px] text-gray-400 border border-gray-200 rounded-lg px-3 py-1 hover:border-red-300 hover:text-red-400 transition-colors"
                        >
                          예약취소
                        </button>
                      )}
                    </div>
                  </div>

                  {/* 상세 정보 그리드 */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 divide-x divide-y divide-[#f5f2f0]">
                    <div className="px-5 py-4">
                      <p className="text-[11px] text-gray-400 mb-1 font-semibold uppercase tracking-wide">예약일</p>
                      <p className="text-[13px] font-bold text-[#3d2f2a]">{formatDate(r.reserve_date)}</p>
                    </div>
                    <div className="px-5 py-4">
                      <p className="text-[11px] text-gray-400 mb-1 font-semibold uppercase tracking-wide">예약 시간</p>
                      <p className="text-[13px] font-bold text-[#3d2f2a]">
                        {r.start_time && r.end_time
                          ? `${formatTime(r.start_time)} ~ ${formatTime(r.end_time)}`
                          : r.label ?? '-'}
                      </p>
                    </div>
                    <div className="px-5 py-4">
                      <p className="text-[11px] text-gray-400 mb-1 font-semibold uppercase tracking-wide">예약 인원</p>
                      <p className="text-[13px] font-bold text-[#3d2f2a]">{r.headcount}명</p>
                    </div>
                    <div className="px-5 py-4">
                      <p className="text-[11px] text-gray-400 mb-1 font-semibold uppercase tracking-wide">예약 번호</p>
                      <p className="text-[13px] font-bold text-[#3d2f2a]">#{r.reservation_id}</p>
                    </div>
                  </div>

                  {/* 경고 배너: 1시간 전인데 대기 상태 */}
                  {warn && (
                    <div className="mx-4 mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-[13px] text-amber-700 flex items-center gap-2">
                      <span>⚠️</span>
                      <span>예약 시간이 임박했지만 아직 예약 대기 상태입니다. 화면 하단 <span className="font-bold">대표번호</span>로 문의해주세요.</span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>


    {cancelModalId && (() => {
      const r = memberReservations.find(r => r.reservation_id === cancelModalId)
      if (!r) return null
      return (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="bg-white rounded-[14px] shadow-[0_8px_32px_rgba(0,0,0,0.15)] px-6 py-7 w-[320px] text-center">
            <p className="text-[15px] font-bold text-[#3d2f2a] mb-2">예약을 취소하시겠어요?</p>
            <div className="bg-[#f9f6f4] rounded-xl px-4 py-3 mb-5 text-[13px] text-[#555] space-y-1 text-left">
              <p><span className="text-gray-400 mr-2">공간</span><span className="font-semibold">{r.space_name}</span></p>
              <p><span className="text-gray-400 mr-2">날짜</span><span className="font-semibold">{formatDate(r.reserve_date)}</span></p>
              <p><span className="text-gray-400 mr-2">시간</span><span className="font-semibold">
                {r.start_time && r.end_time ? `${formatTime(r.start_time)} ~ ${formatTime(r.end_time)}` : r.label ?? '-'}
              </span></p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCancelModalId(null)}
                className="flex-1 h-[42px] border-[1.5px] border-[#e5e7eb] text-gray-500 rounded-lg text-[14px] font-bold hover:bg-gray-50 transition-colors"
              >
                돌아가기
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="flex-1 h-[42px] bg-red-400 text-white border-none rounded-lg text-[14px] font-bold cursor-pointer hover:bg-red-500 transition-colors disabled:bg-red-200"
              >
                {cancelling ? '취소 중...' : '예약 취소'}
              </button>
            </div>
          </div>
        </div>
      )
    })()}
    </>
  )
}